// PRIVATE CONSTANTS
var STATE_CLOSED = 'closed'
var STATE_OPEN = 'open'
var CURRENT_LOCATION_ID = 'current_location'
var PLACEHOLDER_TEXTS = ['From tour stop?', 'To tour stop?']

/**
 * Caches paths to prevent redundant network requests.
 */
var PathCache = {
    _cache: {},

    /**
     * Get cached path from two stops
     */
    get: function(stop0, stop1) {
        return PathCache._cache[stop0.id + '_' + stop1.id] || null
    },

    /**
     * Caches path for two stops
     */
    set: function(path, stop0, stop1) {
        PathCache._cache[stop0.id + '_' + stop1.id] = path
    },
}

/**
 * To/From field structures used internally by the DirectionsController
 * @param {Object} opts Options
 * @param {JQuery} opts.container JQuery "field wrap" container
 * @param {number} opts.index Field index
 * @param {Function} opts.focus Focus callback when field is clicked (ie: focus on click)
 */
function Field(opts) {
    this._container = opts.container

    this._stop = null
    this._title = ''

    // Index can be changed to 0 or 1 during field swap
    this.index = opts.index

    this._updateView()
}
Field.prototype = {
    /**
     * Add focus class to container element
     */
    focus: function() {
        this._container.addClass('focus')
    },

    /**
     * Remove focus class to container element
     */
    blur: function() {
        this._container.removeClass('focus')
    },

    /**
     * Get stop for this field.
     * @return {Object} The stop or null.
     */
    getStop: function() {
        return this._stop
    },

    /**
     * Sets stop and field title, then updates the view/dom.
     * @param {Object} stop Stop
     */
    setStop: function(stop) {
        this._stop = stop || null
        this._title = stop ? stop.title : ''
        this._updateView()
    },

    /**
     * Updates the view/dom text with field title. If field title is empty, uses placeholder text instead.
     */
    _updateView: function() {
        var className = ''
        var title = this._title
        if (!title) {
            title = PLACEHOLDER_TEXTS[this.index]
            className = 'empty'
        }
        this._container
            .find('.text-field')
            .removeClass('empty')
            .addClass(className)
            .text(title)
    },
}

/**
 * Exported controller to manage directions on the map. Used by map controller, but uses callbacks
 * to remove dependencies.
 * @param {Object}   opts                 Options
 * @param {Boolean}  opts.isMobile        Is this being used with the mobile map view?
 * @param {JQuery}   opts.container       JQuery directions module container. Expects certain dom children to work correctly.
 * @param {Array}    opts.stops           Array of stops on map.
 * @param {Function} opts.onOpen          Callback when directions module is opened.
 * @param {Function} opts.onClose         Callback when directions module is closed.
 * @param {Function} opts.onFieldFocus    Callback when a field gets focused.
 * @param {Function} opts.onPath          Callback when path is calculated.
 * @param {Function} opts.onSwap          Callback when fields are swapped.
 */
function DirectionsController(opts) {
    this._isMobile = opts.isMobile

    this._showDirectionsWhenClosed = this._isMobile

    // jquery dom container
    this._container = opts.container
    this._list = opts.list

    // map array of stops by stopid
    this._stops = opts.stops.reduce(function(map, stop) {
        map[stop.id] = stop
        return map
    }, {})

    // required for pathing
    this._calculateDirections = opts.calculateDirections

    // optional callbacks
    this._onOpen = opts.onOpen || function() {}
    this._onClose = opts.onClose || function() {}
    this._onFieldFocus = opts.onFieldFocus || function() {}
    this._onPath = opts.onPath || function() {}
    this._onSwap = opts.onSwap || function() {}
    this._onError = opts.onError || function() {}

    this._state = STATE_CLOSED

    this._fields = [this._createField(0), this._createField(1)]
    this._currentField = null

    if (this._isMobile) {
        // a special stop for mobile
        this._stops[CURRENT_LOCATION_ID] = {
            id: CURRENT_LOCATION_ID,
            title: 'Current Location',
        }

        // set first field to be current location if mobile
        this._fields[0].setStop(this._stops[CURRENT_LOCATION_ID])
    }

    // init bindings
    this._container.on('click', '.toggle-btn', this._onToggleButtonClick.bind(this))
    this._container.on('click', '.swap-btn', this._onSwapButtonClick.bind(this))
}

DirectionsController.prototype = {
    /**
     * Is directions module open?
     * @return {Boolean} True is module is open
     */
    isOpen: function() {
        return this._state === STATE_OPEN
    },

    /**
     * Do both fields have stops?
     * @return {Boolean} True if both fields have stops.
     */
    hasBothStops: function() {
        return this._fields[0].getStop() && this._fields[1].getStop()
    },

    /**
     * Opens directions module.
     */
    open: function() {
        // set state as open and show
        this._state = STATE_OPEN
        this._container.addClass('selected')

        // callback
        this._onOpen()

        // recalc
        this._calculate()
    },

    /**
     * Closes directions module.
     */
    close: function() {
        // set state as closed and hide
        this._state = STATE_CLOSED
        this._container.removeClass('selected')
        this._focusField(null)

        // callback
        this._onClose()
    },

    /**
     * Closes just the list part of the directions module.
     */
    closeList: function() {
        this._focusField(null)
    },

    /**
     * Sets the current field's stop with stop id and recalculates the path.
     * @param {Integer} stopid Stop ID to set for current field.
     */
    setStop: function(stopid) {
        this._list.find('.stop.selected').removeClass('selected')

        if (this._currentField) {
            var stop = this._stops[stopid]

            if (stop) {
                this._list.find('.stop[stopid="' + stop.id + '"]').addClass('selected')

                this._currentField.setStop(stop)
            }
        }

        this._calculate()
    },

    /**
     * Calculates the path from both field stops and returns it via callback from constructor.
     */
    _calculate: function() {
        var stop0 = this._fields[0].getStop()
        var stop1 = this._fields[1].getStop()

        // don't show directions when closed
        if (!this._showDirectionsWhenClosed && this._state !== STATE_OPEN) {
            return
        }

        // don't show directions if any stops are the same or empty
        if (!stop0 || !stop1 || stop0 === stop1) {
            return
        }

        // bypass the cache if one of them is current location
        var useCache = stop0.id !== CURRENT_LOCATION_ID && stop1.id !== CURRENT_LOCATION_ID

        if (useCache) {
            // get cached path
            var cachedPath = PathCache.get(stop0, stop1)
            if (cachedPath) {
                // callback with cached path
                this._onPath(cachedPath)
                return
            }

            // set cache as busy (ie: cache as empty array until async operation finishes - prevent multiple service calls)
            PathCache.set([], stop0, stop1)
        }

        var self = this

        // get origin coords (async if "Current Location")
        this._getLatLngFromStop(stop0, function(origin) {
            // get dest coords (async if "Current Location")
            self._getLatLngFromStop(stop1, function(dest) {
                // get path from start to end
                self._calculateDirections(
                    origin,
                    dest,
                    function(path) {
                        // cache path
                        if (useCache) {
                            PathCache.set(path, stop0, stop1)
                        }

                        // callback - non-cached path
                        self._onPath(path)
                    },
                    function(errorMessage) {
                        self._onError(errorMessage)
                    }
                )
            })
        })
    },

    /**
     * Gets latitude/longitude from stop. If stop is Current Location, current position is queried.
     * @param  {Object}   stop     Stop to get lat/lng from.
     * @param  {Function} callback Success callback with a google maps LatLng returned param.
     */
    _getLatLngFromStop: function(stop, callback) {
        var self = this
        var Analytics = global.Analytics
        if (stop.id === CURRENT_LOCATION_ID) {
            if (Analytics && Analytics.gpsCollector) {
                Analytics.gpsCollector.getPosition(
                    function(gpsObject) {
                        if (Analytics.gpsCollector.isGpsInBounds(gpsObject)) {
                            callback(new google.maps.LatLng(gpsObject.lat, gpsObject.lng))
                        } else {
                            self._onError('You need to be on campus.')
                        }
                    },
                    function(positionError) {
                        self._onError('Unable to get your current position.')
                    }
                )
            } else {
                self._onError('Location services required for Current Location.')
            }
        } else {
            callback(new google.maps.LatLng(stop.reallat, stop.reallng))
        }
    },

    /**
     * Creates a new Field object used internally.
     * @param  {Integer} index 0 or 1 (for .field0 and .field1 classes)
     * @return {Field}         Newly created field.
     */
    _createField: function(index) {
        var self = this

        var $field = this._container.find('.field-wrap.field' + index)

        // toggle field on click
        $field.on('click', function() {
            self._focusField(self._currentField !== field ? field : null)
        })

        var field = new Field({
            container: $field,
            index: index,
            focus: function() {
                self._focusField(field)
            },
        })
        return field
    },

    /**
     * Focuses a field and sets it as current.
     * @param  {Field}   field            Field to focus and set as current.
     */
    _focusField: function(field) {
        var stop = null

        // blur previous field
        if (this._currentField) {
            this._currentField.blur()
            this._list.removeClass('show')
            this._list.find('.stop.selected').removeClass('selected')
        }

        // apply and focus current field
        this._currentField = field
        if (this._currentField) {
            this._currentField.focus()
            this._list.addClass('show')

            stop = this._currentField.getStop()

            if (stop) {
                this._list.find('.stop[stopid="' + stop.id + '"]').addClass('selected')
            }
        }

        // callback
        this._onFieldFocus(stop)
    },

    /**
     * Triggered when toggle button is clicked.
     */
    _onToggleButtonClick: function() {
        if (this.isOpen()) {
            if (this.isMobile && this._list.hasClass('show')) {
                this.closeList()
            } else {
                this.close()
            }
        } else {
            this.open()
        }
    },

    /**
     * Triggered when swap button is clicked.
     */
    _onSwapButtonClick: function() {
        // swap in array
        this._fields.unshift(this._fields.pop())

        // swap field indices
        var tmpIndex = this._fields[0].index
        this._fields[0].index = this._fields[1].index
        this._fields[1].index = tmpIndex

        // update
        this._fields[0].setStop(this._fields[0].getStop())
        this._fields[1].setStop(this._fields[1].getStop())

        // toggle class
        this._container.find('.fields').toggleClass('swapped')

        // recalc
        this._calculate()

        // callback
        this._onSwap(this._currentField ? this._currentField.getStop() : null)
    },
}

// export
export default DirectionsController
